<template>
  <div class="container-fluid">

    <!-- top title -->
    <h1 class="text-center logo-themed-header">
      <LogoSvg class="logo-icon" color="#e1bb28"></LogoSvg>
      <span class="text-my-oscar-list">MY<span class="text-oscar">OSCAR</span>POOL</span>
    </h1>

    <!-- the basic idea -->
    <div id="the-basic-idea" class="mol-center-card-width feature-section">
      <h4 class="feature-section-title text-my-oscar-list"><img :src="`${$imagePathBase}win_icon.svg`" alt="Logo" class="logo-image">THE<span class="text-oscar">BASIC</span>IDEA</h4>
      <p>
        An Oscar Pool is private contest where you and your family, friends or coworkers can compete to see who's picks win on Oscar night.
      </p>
      <p>
        You can create an Oscar Pool and invite friends to join; or join one someone else has created. Once you do, you fill out a ballot much like members of The Academy do. Come Oscar night, you can keep track of the results as the winners are announced.
      </p>
    </div>

    <!-- the full rules -->
    <div id="the-full-rules" class="mol-center-card-width feature-section">
      <h4 class="feature-section-title text-my-oscar-list"><img :src="`${$imagePathBase}win_icon.svg`" alt="Logo" class="logo-image">THE<span class="text-oscar">FULL</span>RULES</h4>

      <!-- running your own pool -->
      <h5 id="running-a-pool">Running Your Own Pool</h5>
      <h6>Pool Creation</h6>
      <p>
        Creating a new Oscar Pool is done from the <router-link to="/oscar-pools">Pools<sup><i class="fa fa-external-link"></i></sup></router-link> page by tapping the <i>Create an Oscar pool</i> box.
      </p>
      <p>
        Give your Oscar Pool a name and description, then hit the <i>create pool</i> button. You'll then be taken to your Oscar Pool's profile page.
      </p>
      <p>
        Your Oscar Pool profile page is "home base" for your pool. You can make modifications to its name and description, and even customize its profile image. Only approved contestants can access this page.
      </p>
      <p>
        At this point, you can invite people to join your pool; but nobody can vote until you specify your contest's <i>scoring setup</i>.
      </p>
      <h6 id="running-a-pool-scoring-setup">Scoring Setup</h6>
      <p>
        For each category, contestants will choose who they think will win. But each category has a point value. For example, choosing a correct BEST PICTURE winner defaults to 100 points, whereas choosing the winning MAKEUP AND HAIRSTYLING nominee is only worth 50 points. You can modify those scores as you like for each category.
      </p>
      <p>
        You also have the option of enabling "second choice" scores. This allows contestants the ability to mark a "second guess" for each category, where winning choices give a reduced number of points. These point values default to half-credit, but you can adjust those as well.
      </p>
      <p>
        Once you're happy with your scoring setup, hit the <i>launch oscar pool</i> button. But be aware, <b>you cannot adjust the score values once you launch your contest</b>.
      </p>
      <h6>Inviting Contestants</h6>
      <p>
        Anyone can apply to be a contestant in your Oscar Pool. "Inviting" someone simply means sharing the link where they can apply to be a contestant. Tapping the <i class="fa fa-user-plus"></i> in the upper-right reveals a QR code you can show to someone, or a link you can send.
      </p>
      <p>
        When a user sends a request to join the pool,you'll see their name on the Oscar Pool profile page in a new section: <i>Contestants Pending Approval</i>. If you choose to approve a user, they'll be added to your pool as a contestant. Any applicant you dismiss can re-apply for another chance.
      </p>
      <h6>Contestant Roles: Owners, Admins and Members</h6>
      <p>
        Being the owner of the Oscar Pool, you have certain privileges the other contestants don't have: you can update its name and other details; you can specify the scoring parameters; you can approve pending contestants and kick other contestants out of the pool. If you want to share those responsibilities with another contestant, you can promote them to an <i>admin</i>, where they'll gain those elevated privileges.
      </p>
      <p>
        Each Oscar Pool can only have a single <i>owner</i>. Owners uniquely have the power to revoke an admin's privileges, demoting them to <i>member</i> status. Owners are also the only members who can delete an Oscar Pool. If, for some reason, an owner wants to choose another admin to replace themselves as owner, they can do that as well.
      </p>
      <h6>Editing Your Pool</h6>
      <p>
        Any <i>admin</i> can edit the Oscar Pool by tapping the <i class="fa fa-pencil-square-o"></i> in the upper-right corner of the Oscar Pool's profile header. This opens a page where most details about the club can be altered. Scoring parameters, as mentioned before, can NOT be altered once the contest has been launched.
      </p>
      <p>
        The <i>owner</i> of the club sees an additional option on the edit page: <i>delete oscar pool</i>. When you delete a pool, it will disappear from view for all members—admins included. You, as the owner, will still see pool on your Pools page for 24 hours, where you'll be given the opportunity to change your mind.
      </p>
      <h6>Being A Contestant</h6>
      <p>
        Beyond that, you're also just another contestant, ready to join the <a href="#competition">competition</a>.
      </p>

      <!-- joining a pool -->
      <h5 id="joining-a-pool">Joining an Existing Pool</h5>
      <h6>Requesting Admittance</h6>
      <p>
        If you want to be a contestant in an existing Oscar Pool, you need to send a request to join. Any current member of the pool can share the unique web url you need to visit to request entry to a pool. Once you've applied, you'll need to wait for an admin of the Oscar Pool to approve your request.
      </p>
      <p>
        You can check the status of your request on the <router-link to="/oscar-pools">Pools<sup><i class="fa fa-external-link"></i></sup></router-link> page.
      </p>
      <p>
        Once admitted, you'll have access to the pool's profile page, ready to join the <a href="#competition">competition</a>.
      </p>

      <!-- the competition -->
      <h5 id="competition">Competition</h5>
      <h6 id="competition-scoring">Scoring</h6>
      <p>
        Your ballot lists each nomination category (BEST PICTURE, DIRECTING, etc.), along with the list of nominees for that category (the movie title, the actress, the song, etc.). But every vote isn't weighted the same. Depending on how the pool's admins set up the scoring some categories can be weighted heavier—worth more points—than others. For example, the default value of a correct BEST PICTURE guess is 100 points; whereas guessing the right MAKEUP AND HAIRSTYLING winner is only worth 50 points. The admins of your Oscar Pool can tune these point values to their liking.
      </p>
      <p>
        Your ballot <i>might</i> also offer a lesser point value for your "second choice". If <i>that</i> nominee wins, you'll be awarded that smaller point value. This can make the contest a little more exiting for some, so it's an option.
      </p>

      <h6>Voting</h6>
      <p>
        Your Oscar Pool's profile page is where you can find a link to your official ballot—but only after an admin sets up the scoring parameters for the pool and officially launches the competition.
      </p>
      <p>
        As explained in <a href="#competition-scoring">scoring</a>, the ballot is sectioned into categories. Each category lists the nominees; each nominee has a checkbox, used to select who you think will win in that category. As mentioned, there might be two checkboxes per nominee, if a second-choice option is available.
      </p>
      <p>
        You can modify your ballot right up until the official start of the ceremony. At that point, you won't be able to modify your choices. Your ballot has a countdown timer to remind you of this.
      </p>

      <h6>Results Leaderboard</h6>
      <p>
        Your Oscar Pool has a private leaderboard listing each contestant and their current score. Winners will be updated in real-time during the ceremony; and the leaderboard will remain viewable long after the ceremony is over. Contestants with the same score will be listed together, occupying the same "place" in the board.
      </p>

      <h6>Bonus Leaderboard: Race-To-See-The-Most!</h6>
      <p>
        myOscarList is, at its core, all about <i>watching</i> the movies. So don't forget to mark your viewed films on the <router-link to="/checklist">Checklist<sup><i class="fa fa-external-link"></i></sup></router-link> page. Your Oscar Pool profile page will list an ongoing sorted list as contestants see who can watch the most nominees before teh ceremony.
      </p>
    </div>

  </div> <!-- container -->
</template>


<script>



import LogoSvg from "@/components/icons/LogoSvg.vue";

export default {
  name: "AboutOscarPoolsPage",
  components: {
    LogoSvg
  },
}

</script>


<style scoped>

.logo-themed-header {
  color: var(--mol-color-gold);
  text-align: center;
  margin-top: 10px;
  margin-bottom: 20px;
  font-size: xxx-large;

  svg {
    height: 1.125em;
    margin-right: 0.1em;
    margin-top: -0.1275em;
  }
}

.feature-section {
  color: var(--mol-color-win-icon);
  background-color: var(--mol-color-gold-background);
  overflow: hidden;
  margin-bottom: 40px;
  padding: 2em;

  border: solid;
  border-width: 3px;
  border-color: var(--mol-color-gold);
  border-radius: 0.375rem;

  .feature-section-title {
    color: var(--mol-color-win-icon);
    text-align: center;
    margin-bottom: 1em;
  }

  .insight-box {
    font-weight: 500;
    font-size: smaller;
    text-align: center;
    border: solid;
    border-width: thin;
    border-color: color-mix(in srgb, var(--mol-color-gold), transparent 50%);
    padding: 1em;

    margin-right: auto;
    margin-left: auto;
  }

  .feature-section-about-the-list {
    text-align: center;
  }
}

.text-my-oscar-list {
  text-align: center;
  font-weight: 300;

  .text-oscar {
    font-weight: 600;
    padding-left: 0.05em;
    padding-right: 0.05em;
  }

  img {
    height: 1em;
    margin-right: 0.1em;
    margin-top: -0.2em;
  }
}


a {
  color: inherit;
  text-decoration: inherit;
  font-weight: 500;

  .fa-external-link, fa-anchor {
    margin-left: 0.2em;
  }
  &:hover {
    color: var(--bs-link-hover-color);
  }
}

h5 {
  text-transform: uppercase;
  margin-top:2.25em;
}

h6 {
  margin-top: 1.5em;
}

</style>